import { computed, shallowRef } from 'vue';
import { acceptHMRUpdate, defineStore } from 'pinia';
import {
    getImpersonation,
    startImpersonation,
    stopImpersonation,
} from '@/services/superAdminService';

export interface CreditorAccessContext {
    creditorId: number;
    creditorName: string;
}

// eslint-disable-next-line import/prefer-default-export
export const useSuperAdminCreditorAccessStore = defineStore('superAdminCreditorAccess', () => {
    const context = shallowRef<CreditorAccessContext | null>(null);
    const isHydrating = shallowRef(false);
    let hydratePromise: Promise<void> | null = null;

    const isActive = computed(() => context.value !== null);
    const creditorName = computed(() => context.value?.creditorName ?? '');

    function setContext(creditor: CreditorAccessContext | null): void {
        context.value = creditor;
    }

    function clearAccess(): void {
        context.value = null;
    }

    /**
     * Load impersonation state from the HTTP-only cookie (via API).
     * Call after auth user is known to be a superadmin (page refresh / sign-in).
     * Concurrent callers share one in-flight request.
     */
    async function hydrateAccess(): Promise<void> {
        if (hydratePromise) {
            return hydratePromise;
        }

        hydratePromise = (async () => {
            try {
                isHydrating.value = true;
                const impersonation = await getImpersonation();

                if (!impersonation) {
                    clearAccess();
                    return;
                }

                setContext({
                    creditorId: impersonation.id,
                    creditorName: impersonation.name,
                });
            } catch (error) {
                console.error(error);
                clearAccess();
            } finally {
                isHydrating.value = false;
                hydratePromise = null;
            }
        })();

        return hydratePromise;
    }

    async function startAccess(creditor: CreditorAccessContext): Promise<void> {
        await startImpersonation(creditor.creditorId);
        setContext({
            creditorId: creditor.creditorId,
            creditorName: creditor.creditorName,
        });
    }

    async function exitAccess(): Promise<void> {
        try {
            await stopImpersonation();
        } finally {
            clearAccess();
        }
    }

    return {
        context,
        isHydrating,
        isActive,
        creditorName,
        hydrateAccess,
        startAccess,
        exitAccess,
        clearAccess,
    };
});

if (import.meta.webpackHot) {
    import.meta.webpackHot.accept(
        acceptHMRUpdate(useSuperAdminCreditorAccessStore, import.meta.webpackHot),
    );
}
